import React, {useEffect} from 'react';

import find from 'lodash/find';
import sortBy from 'lodash/sortBy';

import {useSetAcceptanceVersionViewed} from 'app/acceptance/api';
import {ApprovedStamp} from 'app/acceptance/components/ApprovedStamp';
import {AcceptSidebarActions} from 'app/acceptance/components/GenericAcceptSidebar/AcceptSidebarActions';
import {CommentList} from 'app/comments/components/CommentList';
import {UserAvatar} from 'app/components/general/avatars';
import ElapsedTime from 'app/components/general/ElapsedTime';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {getElapsedTime} from 'app/general/dates';
import {gt} from 'app/utils/i18n';

import type {
  AcceptanceSerializedType,
  AcceptanceStatusSerializedType,
  BaseDocumentContentType,
  BasicUserSerializedType,
} from 'sweet-types';

type HeaderProps = {
  acceptance: AcceptanceSerializedType;
};

const AcceptHeader = ({acceptance}: HeaderProps) => (
  <div className="border-top border-bottom bg-light p-2">
    <div className="d-flex">
      {acceptance.requester != null ? (
        <UserAvatar user={acceptance.requester} showCard />
      ) : null}
      <div className="ml-2">
        <div className="small">
          <div>{gt('Requested by')}</div>
          <b>
            {acceptance.requester != null
              ? acceptance.requester.name
              : gt('Deleted user')}
          </b>
        </div>
        <div className="small my-2 text-break">{acceptance.message}</div>
        <div className="small text-muted font-italic">
          <ElapsedTime
            timestamp={acceptance.created_at}
            tooltipPlacement="top"
          />
        </div>
      </div>
    </div>
  </div>
);

type StatusesProps = {
  statuses: Array<AcceptanceStatusSerializedType>;
};

const AcceptStatuses = ({statuses}: StatusesProps) => {
  const pending = sortBy(
    statuses.filter((s) => !s.status),
    ['name']
  );
  const accepted = sortBy(
    statuses.filter((s) => s.status),
    ['name']
  );
  return (
    <>
      {accepted.length > 0 ? (
        <>
          <div className="font-weight-bold px-2 py-1 small mt-3 border-bottom">
            {gt('Confirmed')}
          </div>
          {accepted.map((status) => (
            <LazyTooltip
              key={status.id}
              tooltip={
                <span className="small">
                  {gt('%(name)s confirmed %(ago)s', {
                    name: status.user.name,
                    ago:
                      status.accepted_at != null
                        ? getElapsedTime(status.accepted_at)
                        : '',
                  })}
                </span>
              }
            >
              <div className="px-2 py-1 border-bottom d-flex align-items-center">
                <UserAvatar
                  small={true}
                  user={status.user}
                  tooltipTitle={null}
                />
                <div className="pl-1 text-truncate small flex-grow-1">
                  {status.user.name}
                </div>
                <i className="text-success far fa-thumbs-up fa-fw ml-1" />
              </div>
            </LazyTooltip>
          ))}
        </>
      ) : null}
      {pending.length > 0 ? (
        <>
          <div className="font-weight-bold px-2 py-1 small mt-3 border-bottom">
            {gt('Pending')}
          </div>
          {pending.map((status) => (
            <LazyTooltip
              key={status.id}
              tooltip={
                <span className="small">
                  {status.seen_at != null
                    ? gt('%(name)s visited %(ago)s', {
                        name: status.user.name,
                        ago: getElapsedTime(status.seen_at),
                      })
                    : gt('%(name)s has not seen these changes.', {
                        name: status.user.name,
                      })}
                </span>
              }
            >
              <div className="px-2 py-1 border-bottom d-flex align-items-center">
                <UserAvatar
                  small={true}
                  user={status.user}
                  tooltipTitle={null}
                />
                <div className="pl-1 text-truncate small flex-grow-1">
                  {status.user.name}
                </div>
                {status.seen_at != null ? (
                  <i className="text-muted far fa-eye fa-fw ml-1" />
                ) : null}
              </div>
            </LazyTooltip>
          ))}
        </>
      ) : null}
    </>
  );
};

interface Props {
  acceptance: AcceptanceSerializedType;
  documentId: number;
  documentType: BaseDocumentContentType;
  documentHtmlURL: string;
  versionURL: string;
  versionApprovedAt: string | null;
  versionApprovedBy: BasicUserSerializedType | null;
  userId: number;
  nextReviewDate: string | null;
}

export const AcceptTable = ({
  acceptance,
  userId,
  documentId,
  documentHtmlURL,
  versionURL,
  versionApprovedAt,
  versionApprovedBy,
  nextReviewDate,
  documentType,
}: Props) => {
  const [setAcceptanceVersionViewed] = useSetAcceptanceVersionViewed();

  useEffect(() => {
    const currentUserStatus = find(
      acceptance.accept_statuses,
      (status) => status.user.id === userId
    );

    if (currentUserStatus != null && currentUserStatus.seen_at == null) {
      setAcceptanceVersionViewed({
        id: currentUserStatus.id,
        document_id: documentId,
        content_type: 'procedure',
      });
    }
  }, [userId]);

  const status: AcceptanceStatusSerializedType | void =
    acceptance.accept_statuses.filter((status) => status.user.id === userId)[0];

  return (
    <>
      <AcceptHeader acceptance={acceptance} />
      {versionApprovedAt != null && versionApprovedBy != null ? (
        <ApprovedStamp
          versionApprovedAt={versionApprovedAt}
          versionApprovedBy={versionApprovedBy}
        />
      ) : null}
      <AcceptStatuses statuses={acceptance.accept_statuses} />
      {status != null ? (
        <AcceptSidebarActions
          status={status}
          acceptance={acceptance}
          documentId={documentId}
          versionApprovedAt={versionApprovedAt}
          versionURL={versionURL}
          documentHtmlURL={documentHtmlURL}
          nextReviewDate={nextReviewDate}
        />
      ) : null}
      <div className="font-weight-bold px-2 py-1 small mt-3 border-bottom">
        <i className="far fa-comments mr-1" />
        {gt('Discussion')}
      </div>
      <CommentList
        canDelete={false}
        contentType={documentType}
        objectId={documentId}
        noResultMessage={
          <NoResultsMessage
            icon="fa-comments"
            head={gt('No comments')}
            onSidebar={true}
          >
            {gt('Use the form below to add a comment')}
          </NoResultsMessage>
        }
      />
    </>
  );
};
